import React, { createContext, useContext, useReducer, useCallback, useMemo } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
console.log(API_BASE_URL);
/**
 * Helper: Converts base64 DataURL to a Blob.
 * Necessary for Multer upload.single('sourceImage')
 */
const dataURLtoBlob = (dataurl) => {
  if (!dataurl) return null;
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

const getEmailFromStorage = () => {
  try {
    const userData = localStorage.getItem('tubbz_user');
    if (userData) {
      const parsed = JSON.parse(userData);
      return parsed.email || '';
    }
  } catch (e) {
    return '';
  }
  return '';
};

const getUserNameFromStorage = () => {
  try {
    const userData = localStorage.getItem('tubbz_user');
    if (userData) {
      const parsed = JSON.parse(userData);
      if (parsed.name) return parsed.name;
    }
    return localStorage.getItem('tubbz_user_name') || '';
  } catch (e) {
    return '';
  }
};

const getEventModeFromStorage = () => {
  try {
    return localStorage.getItem('tubbz_event_mode') === 'true';
  } catch (e) {
    return false;
  }
};

const getSkipEmailFromStorage = () => {
  try {
    return localStorage.getItem('tubbz_event_skip_email') === 'true';
  } catch (e) {
    return false;
  }
};

const initialState = {
  capturedPhoto: null,        
  generatedImageUrl: null,
  generatedbaseUrl: null,    
  generationProgress: 0,
  customisation: { outfit: null, accessories: [], background: null },
  loading: false,
  error: null,
  tubbzId: null,
  userEmail: getEmailFromStorage(), 
  userName: getUserNameFromStorage(),
  eventMode: getEventModeFromStorage(),
  skipEmailInEventMode: getSkipEmailFromStorage(),
  marketingConsent: false,
  emailStatus: 'idle'
};

const TubbzContext = createContext();


export function TubbzProvider({ children }) {
  const [state, dispatch] = useReducer((s, a) => ({ ...s, ...a }), initialState);
  
  const resetSession = useCallback(() => {
    dispatch({
      ...initialState,
      userEmail: getEmailFromStorage(), // Keep the email for the next attempt
      userName: getUserNameFromStorage(),
      capturedPhoto: null,
      generatedImageUrl: null,
      generatedbaseUrl: null,
      tubbzId: null
    });
    localStorage.removeItem('tubbz_user_name');
  }, []);

  const setEmail = useCallback((email) => {
    dispatch({ userEmail: email });
  }, []);

  const setUserName = useCallback((name) => {
    dispatch({ userName: name });
    try {
      localStorage.setItem('tubbz_user_name', name || '');
    } catch (e) {}
  }, []);

  const setEventMode = useCallback((val) => {
    const isEvent = !!val;
    dispatch({ eventMode: isEvent });
    try {
      localStorage.setItem('tubbz_event_mode', isEvent ? 'true' : 'false');
    } catch (e) {}
  }, []);

  const setSkipEmailInEventMode = useCallback((val) => {
    const isSkip = !!val;
    dispatch({ skipEmailInEventMode: isSkip });
    try {
      localStorage.setItem('tubbz_event_skip_email', isSkip ? 'true' : 'false');
    } catch (e) {}
  }, []);

  const setConsent = useCallback((val) => {
    dispatch({ marketingConsent: !!val });
  }, []);

  const subscribeEmail = useCallback(async (email) => {
    dispatch({ loading: true, error: null });
    try {
      const response = await axios.post(`${API_BASE_URL}/api/subscribe`, { email });
      dispatch({ loading: false, userEmail: email });
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to subscribe. Please try again.";
      dispatch({ error: msg, loading: false });
      throw new Error(msg);
    }
  }, []);

  const validatePhoto = useCallback(async (base64) => {
    dispatch({ loading: true, error: null });
    try {
      const token = localStorage.getItem('authToken');
      const formData = new FormData();
      const imageBlob = dataURLtoBlob(base64);
      
      formData.append('sourceImage', imageBlob, 'capture.png');

      const response = await axios.post(
        `${API_BASE_URL}/api/validatephoto/validate-photo`, 
        formData, 
        {
          headers: { 
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          },
          withCredentials: true 
        }
      );

      if (response.data && response.data.isValid) {
        dispatch({ capturedPhoto: base64, loading: false });
        return true;
      } else {
        // If API says invalid, dispatch the specific reason
        const errorMsg = response.data.message || "No face detected. Please try again.";
        dispatch({ error: errorMsg, loading: false, capturedPhoto: null });
        return false;
      }
    } catch (err) {
      // Handle Network/Server errors
      const msg = err.response?.data?.message || "Validation service error. Please try again.";
      dispatch({ error: msg, loading: false, capturedPhoto: null });
      return false;
    }
  }, []);

  const resendTubbzEmail = useCallback(async (customEmail = null) => {
    dispatch({ loading: true, error: null, emailStatus: 'sending' });

    try {
      let targetEmail = customEmail || state.userEmail || getEmailFromStorage();
      if (!targetEmail) {
        try {
          targetEmail = localStorage.getItem('tubbz_guest_email') || localStorage.getItem('tubbz_latest_email') || '';
        } catch (e) {}
      }

      if (!targetEmail) {
        const msg = "No email address found. Please enter your email address.";
        dispatch({ error: msg, loading: false, emailStatus: 'error' });
        throw new Error(msg);
      }

      const response = await axios.post(`${API_BASE_URL}/api/subscribe/generation`, {
        email: targetEmail,
        imagePath: state.generatedImageUrl
      });

      if (response.data && (response.data.success || response.status === 200)) {
        dispatch({ emailStatus: 'sent', loading: false });
        return true;
      } else {
        throw new Error(response.data?.message || "Failed to resend email.");
      }
    } catch (err) {
      const msg = err.message || err.response?.data?.message || "Failed to resend email.";
      dispatch({ error: msg, loading: false, emailStatus: 'error' });
      throw err;
    }
  }, [state.userEmail, state.generatedImageUrl]);

  /**
   * 2. GENERATE TUBBZ
   * Matches your Controller's req.body and req.file expectations exactly.
   */
  const uploadAndGenerate = useCallback(async (customName = "My Tubbz Duck") => {
    if (state.loading) {
      console.warn("Generation request already in flight.");
      return;
    }

    dispatch({ loading: true, error: null, generationProgress: 5, generationFailed: false });
    
    try {
      console.log(state.customisation);
      const userString = localStorage.getItem('tubbz_user');
      const currentUser = JSON.parse(userString || '{}');
      const token = localStorage.getItem('authToken');
      const formData = new FormData();
      
      const imageBlob = dataURLtoBlob(state.capturedPhoto);
      if (!imageBlob) throw new Error("Please take a photo first.");
      
      formData.append("sourceImage", imageBlob, 'tubbz_source.png');
      formData.append("duckName", customName);
      if (state.userName) {
        formData.append("userName", state.userName);
      }
      
      formData.append("background", JSON.stringify(state.customisation.background || {}));
      
      const outfitData = state.customisation.outfit || {};
      formData.append("outfit", JSON.stringify(outfitData));
      formData.append("accessories", JSON.stringify(state.customisation.accessories));
      if (currentUser._id) {
        formData.append("userId", currentUser._id);
      }
      dispatch({ generationProgress: 10 });

      const response = await axios.post(`${API_BASE_URL}/api/tubbz/generate-tubbz`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "Authorization": `Bearer ${token}`
        },
        timeout: 180000,
        withCredentials: true
      });

      const responseData = response.data;

      if (responseData.success) {
        const finalbaseUrl = responseData.image;
        const finalUrl = responseData.imageUrl;
        const generatedId = responseData.tubbz?._id || null;

        try {
          localStorage.setItem('tubbz_latest_result', JSON.stringify({
            id: generatedId,
            imageUrl: finalUrl,
            imageBase64: finalbaseUrl,
            duckName: responseData.tubbz?.duckName || 'Official TUBBZ'
          }));
        } catch (e) {
          console.warn("Could not save tubbz_latest_result:", e);
        }

        dispatch({ 
          generatedImageUrl: finalUrl, 
          generatedbaseUrl: finalbaseUrl,
          tubbzId: generatedId,
          generationProgress: 100,
          loading: false,
          generationFailed: false,
          error: null
        });
        
        return finalUrl;
      }

    } catch (err) {
      console.error("Context Generation Error:", err);
      const msg = err.response?.data?.message || err.message || "Generation request failed.";
      // Retain session state (captured photo, customisations, email) while setting error flag
      dispatch({ error: msg, loading: false, generationProgress: 0, generationFailed: true });
      throw new Error(msg);
    }
  }, [state.loading, state.capturedPhoto, state.customisation, state.userName]);


  const deliverEmail = useCallback(async (customEmail = null) => {
    dispatch({ emailStatus: 'idle' });

    // Extract email from: 1. Argument 2. State email 3. Storage
    let finalEmail = customEmail || state.userEmail || getEmailFromStorage();
    if (!finalEmail) {
      try {
        finalEmail = localStorage.getItem('tubbz_guest_email') || localStorage.getItem('tubbz_latest_email') || '';
      } catch (e) {}
    }

    if (!finalEmail) {
      const msg = "No email address found. Please enter your email address.";
      dispatch({ emailStatus: 'error', error: msg });
      throw new Error(msg);
    }

    // Update global state so Final Page knows which email is being used
    dispatch({ 
      userEmail: finalEmail,
      emailStatus: 'updated'
    });

    return finalEmail;
  }, [state.userEmail]);

  const updateCustomisation = useCallback((c) => {
    dispatch({ customisation: { ...state.customisation, ...c } });
  }, [state.customisation]);

  const setGenerationProgress = useCallback((p) => {
    dispatch({ generationProgress: p });
  }, []);

  const setCapturedPhoto = useCallback((photo) => {
    dispatch({ capturedPhoto: photo });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ error: null });
  }, []);

  const value = useMemo(() => ({
    ...state,
    validatePhoto,
    resendTubbzEmail,
    uploadAndGenerate,
    deliverEmail,
    resetSession,
    setEmail,
    setUserName,
    setEventMode,
    setSkipEmailInEventMode,
    setConsent,
    subscribeEmail,
    updateCustomisation,
    setGenerationProgress,
    setCapturedPhoto,
    clearError
  }), [
    state,
    validatePhoto,
    resendTubbzEmail,
    uploadAndGenerate,
    deliverEmail,
    resetSession,
    setEmail,
    setUserName,
    setEventMode,
    setSkipEmailInEventMode,
    setConsent,
    subscribeEmail,
    updateCustomisation,
    setGenerationProgress,
    setCapturedPhoto,
    clearError
  ]);

  return <TubbzContext.Provider value={value}>{children}</TubbzContext.Provider>;
}

export const useTubbz = () => useContext(TubbzContext);